/**
 * Session rank comparison — pure helpers for reading one session's ranking
 * next to another's. Two sessions are comparable when they ranked at least
 * one of the same items, whatever tool each used.
 */

export interface RankComparisonRow {
  itemId: string;
  /** 1-based rank in each session's full list. */
  rankHere: number;
  rankThere: number;
  /** 1-based rank counting only the items both sessions ranked. */
  sharedRankHere: number;
  sharedRankThere: number;
  /** sharedRankHere − sharedRankThere. Positive = the other session ranked it higher. */
  sharedDifference: number;
}

/** Items both sessions ranked, in this session's order. */
export function compareSessionRankings(hereIds: string[], thereIds: string[]): RankComparisonRow[] {
  const thereRank = new Map(thereIds.map((id, i) => [id, i + 1]));
  const shared = hereIds.filter((id) => thereRank.has(id));
  const sharedThere = thereIds.filter((id) => shared.includes(id));
  const sharedRankThere = new Map(sharedThere.map((id, i) => [id, i + 1]));
  return shared.map((itemId, i) => {
    const there = sharedRankThere.get(itemId) ?? 0;
    return {
      itemId,
      rankHere: hereIds.indexOf(itemId) + 1,
      rankThere: thereRank.get(itemId) ?? 0,
      sharedRankHere: i + 1,
      sharedRankThere: there,
      sharedDifference: i + 1 - there,
    };
  });
}

/** How many items two rankings have in common. */
export function sharedItemCount(hereIds: string[], thereIds: string[]): number {
  const there = new Set(thereIds);
  return hereIds.filter((id) => there.has(id)).length;
}
